import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { SHIPPING_FLAT } from "../mock/mock";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, discount, total, clear } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="bg-[#0b0b0c] border-white/10 w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 h-16 border-b border-white/10 flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-display text-xl tracking-wide text-white">
            YOUR BAG
          </SheetTitle>
          <SheetDescription className="sr-only">Items currently in your shopping bag</SheetDescription>
          <span className="text-xs uppercase tracking-widest text-white/45">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </span>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <ShoppingBag size={40} className="text-white/30" />
            <p className="text-white/60">Your bag is empty.</p>
            <Link
              to="/shop"
              onClick={() => setIsOpen(false)}
              className="bg-neon text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-full hover:brightness-95 transition"
            >
              Shop the Drop
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <img
                    src={it.image}
                    alt={it.name}
                    className="w-20 h-24 object-cover rounded-md border border-white/10"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">{it.brand}</p>
                    <h4 className="text-sm font-semibold text-white truncate">{it.name}</h4>
                    <p className="text-xs text-white/50 mt-0.5">Size {it.size}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-white/15 rounded-full">
                        <button
                          onClick={() => updateQty(it.key, it.qty - 1)}
                          className="px-2 py-1.5 text-white/70 hover:text-neon"
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-7 text-center text-sm text-white">{it.qty}</span>
                        <button
                          onClick={() => updateQty(it.key, it.qty + 1)}
                          className="px-2 py-1.5 text-white/70 hover:text-neon"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <span className="text-sm font-bold text-white">${it.price * it.qty}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(it.key)}
                    className="text-white/40 hover:text-trash self-start"
                    aria-label="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                onClick={clear}
                className="text-xs uppercase tracking-widest text-white/40 hover:text-white/70 transition"
              >
                Clear bag
              </button>
            </div>

            <div className="border-t border-white/10 px-5 py-5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm uppercase tracking-widest text-white/60">Subtotal</span>
                <span className="text-sm text-white/80">${subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-center justify-between" data-testid="cart-bundle-savings">
                  <span className="text-sm uppercase tracking-widest text-neon">Bundle savings</span>
                  <span className="text-sm font-semibold text-neon">-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm uppercase tracking-widest text-white/60">Total</span>
                <span className="text-lg font-bold text-white" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <p className="text-[11px] text-white/40">Flat ${SHIPPING_FLAT.toFixed(2)} shipping added at checkout.</p>
              <button
                onClick={checkout}
                className="w-full bg-neon text-black font-bold uppercase tracking-widest text-sm py-4 rounded-full hover:brightness-95 transition"
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
